/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Suspense, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui/components/shadcn/dialog';
import { Cross2Icon } from '@/shared/ui/radix-icons';
import { UnstyledButton, withAuthRedirect } from '@/shared/ui';
import { PageSEO } from '@/shared/seo';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { useAuthenticatedImage } from '@/shared/hooks/use-authenticated-image';
import { useAuth } from '@context/authentication';
import { cn } from '@/shared/ui/utils/tailwind';
import UserSettingsTabs from './UserSettingsTabs';
import {
  UserSettingsIdentity,
  UserSettingsLanguage,
  UserSettingsSignOut,
  normalizeLanguage,
} from './UserSettingsAside';

/** Where to land when the modal is closed without a screen behind it. */
const CLOSE_FALLBACK = '/app/workspace';

/**
 * Loading placeholder for user-settings panes: a title bar, three stacked
 * field rows, and a button block shown while a lazily-loaded settings route
 * resolves.
 *
 * @returns The user-settings skeleton.
 */
const UserSettingsSkeleton = () => (
  <div
    className="w-full max-w-2xl flex flex-col gap-(--space-6)"
    data-testid="user-settings-skeleton"
  >
    <div className="h-6 w-40 animate-pulse rounded-(--radius-md) bg-[var(--skeleton-base)]" />
    <div className="flex flex-col gap-(--space-4)">
      <div className="h-10 w-full animate-pulse rounded-(--radius-md) bg-[var(--skeleton-base)]" />
      <div className="h-10 w-full animate-pulse rounded-(--radius-md) bg-[var(--skeleton-base)]" />
      <div className="h-10 w-3/4 animate-pulse rounded-(--radius-md) bg-[var(--skeleton-base)]" />
    </div>
    <div className="h-10 w-32 animate-pulse rounded-(--radius-md) bg-[var(--skeleton-base)]" />
  </div>
);

/**
 * Navigates out of the user-settings modal: back to the previous screen when
 * one exists in this session's history, otherwise to the workspace.
 *
 * @returns A stable close handler.
 */
const useCloseSettings = () => {
  const navigate = useNavigate();

  return useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) {
      navigate(-1);
      return;
    }
    navigate(CLOSE_FALLBACK, { replace: true });
  }, [navigate]);
};

/**
 * Reads the display identity of the signed-in user from the auth profile.
 *
 * @returns Full name, email and resolved profile picture URL.
 */
const useUserIdentity = () => {
  const auth = useAuth();
  const profile = auth.user?.profile as Record<string, unknown> | undefined;
  const photoUrl = useAuthenticatedImage(profile?.picture as string | undefined);

  return {
    fullName:
      (profile?.full_name as string) ||
      (profile?.name as string) ||
      (profile?.email as string) ||
      '',
    email: (profile?.email as string) || '',
    photoUrl,
    signOut: () => auth.signout(),
  };
};

/**
 * User account settings as a single tabbed modal over the current screen:
 * identity, the settings panes (profile, security, preferences, WhatsApp,
 * help), language selection and sign out all live in this one dialog.
 *
 * @returns The user-settings modal.
 */
const UserSettingsModal = () => {
  const { t, i18n: i18nInstance } = useTranslation('@pages/user-settings');
  const isMobile = useIsMobile();
  const close = useCloseSettings();
  const { fullName, email, photoUrl, signOut } = useUserIdentity();
  const currentLanguage = normalizeLanguage(i18nInstance.language);

  const accountBlocks = (
    <>
      <UserSettingsLanguage currentLanguage={currentLanguage} />
      <UserSettingsSignOut onSignOut={signOut} />
    </>
  );

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent
        data-testid="user-settings-modal"
        aria-describedby={undefined}
        className={cn(
          'p-0 gap-0 overflow-hidden flex flex-col',
          'bg-(--surface-secondary) border-(--border-subtle)',
          isMobile
            ? 'w-screen max-w-none h-[100dvh] rounded-none'
            : 'max-w-4xl w-[calc(100vw-var(--space-8))] h-[min(680px,calc(100dvh-var(--space-16)))]',
        )}
      >
        <PageSEO title={t('nav.settings')} />
        <header
          className="flex items-center justify-between shrink-0 px-(--space-5) py-(--space-4) border-b border-(--border-subtle)"
          style={{ background: 'var(--surface-primary)' }}
        >
          <DialogTitle className="font-(--font-bold)">{t('nav.settings')}</DialogTitle>
          <UnstyledButton
            onClick={close}
            aria-label={t('nav.close')}
            title={t('nav.close')}
            className="flex items-center justify-center cursor-pointer rounded-(--radius-md) h-8 w-8"
            style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none' }}
          >
            <Cross2Icon width={18} height={18} />
          </UnstyledButton>
        </header>

        <div className={cn('flex flex-1 min-h-0', isMobile ? 'flex-col' : 'flex-row')}>
          {isMobile ? (
            <UserSettingsIdentity fullName={fullName} email={email} photoUrl={photoUrl} />
          ) : (
            <aside
              data-testid="user-settings-rail"
              className="shrink-0 w-[232px] flex flex-col border-r border-(--border-subtle) overflow-y-auto p-(--space-2)"
            >
              <UserSettingsIdentity fullName={fullName} email={email} photoUrl={photoUrl} />
              <UserSettingsTabs orientation="vertical" />
              <div className="mt-auto pt-(--space-4) flex flex-col gap-(--space-1)">
                {accountBlocks}
              </div>
            </aside>
          )}

          {isMobile && <UserSettingsTabs orientation="horizontal" />}

          <div
            data-testid="user-settings-pane"
            className="flex-1 min-w-0 overflow-y-auto px-(--space-5) py-(--space-5)"
          >
            <Suspense fallback={<UserSettingsSkeleton />}>
              <Outlet />
            </Suspense>

            {isMobile && (
              <div className="mt-(--space-8) pt-(--space-4) border-t border-(--border-subtle) flex flex-col gap-(--space-1)">
                {accountBlocks}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default withAuthRedirect(UserSettingsModal);
